import { type UseInfiniteQueryOptions, type UseQueryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  type SearchCollectionsV1WorkspacesWorkspaceIdAssetsImagesCollectionsGetData,
  type SearchCollectionsV1WorkspacesWorkspaceIdAssetsImagesCollectionsGetError,
  type SearchCollectionsV1WorkspacesWorkspaceIdAssetsImagesCollectionsGetResponse,
  searchCollectionsV1WorkspacesWorkspaceIdAssetsImagesCollectionsGet,
} from '../../generated';
import type {
  SearchCollectionsInfiniteQueryVariables,
  SearchCollectionsQueryVariables,
} from './search-collections.types';

export type {
  SearchCollectionsInfiniteQueryVariables,
  SearchCollectionsQueryVariables,
} from './search-collections.types';

export type SearchCollectionsQuery = {
  data: SearchCollectionsV1WorkspacesWorkspaceIdAssetsImagesCollectionsGetResponse;
  total_count: number;
};

function buildSearchCollectionsQuery(
  variables: SearchCollectionsQueryVariables,
): SearchCollectionsV1WorkspacesWorkspaceIdAssetsImagesCollectionsGetData['query'] {
  const { workspace_id: _workspaceId, ...query } = variables;
  return query;
}

function buildSearchCollectionsQueryKeyVariables(variables: SearchCollectionsInfiniteQueryVariables) {
  return variables;
}

/**
 * Extracts the total_count from the Vizit-Total-Count response header
 */
function extractTotalCount(response: Response): number {
  const totalCountHeader = response.headers.get('vizit-total-count');
  return totalCountHeader ? Number.parseInt(totalCountHeader, 10) : 0;
}

export const useSearchCollectionsQuery = <
  TData = SearchCollectionsQuery,
  TError = SearchCollectionsV1WorkspacesWorkspaceIdAssetsImagesCollectionsGetError,
>(
  variables: SearchCollectionsQueryVariables,
  options?: Omit<UseQueryOptions<SearchCollectionsQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  const queryKeyVariables = buildSearchCollectionsQueryKeyVariables(variables);

  return useQuery<SearchCollectionsQuery, TError, TData>({
    queryKey: ['SearchCollections', queryKeyVariables],
    queryFn: async () => {
      const { data, response } = await searchCollectionsV1WorkspacesWorkspaceIdAssetsImagesCollectionsGet({
        path: { workspace_id: variables.workspace_id },
        query: buildSearchCollectionsQuery(variables),
        throwOnError: true,
      });
      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    ...options,
  });
};

useSearchCollectionsQuery.getKey = (variables: SearchCollectionsQueryVariables) => [
  'SearchCollections',
  { ...buildSearchCollectionsQueryKeyVariables(variables), offset: variables.offset },
];

useSearchCollectionsQuery.fetcher = (variables: SearchCollectionsQueryVariables, _headers?: RequestInit['headers']) => {
  return async () => {
    const { data, response } = await searchCollectionsV1WorkspacesWorkspaceIdAssetsImagesCollectionsGet({
      path: { workspace_id: variables.workspace_id },
      query: buildSearchCollectionsQuery(variables),
      throwOnError: true,
    });
    return {
      data: data ?? [],
      total_count: extractTotalCount(response),
    };
  };
};

export const useSearchCollectionsInfiniteQuery = <
  TData = SearchCollectionsQuery,
  TError = SearchCollectionsV1WorkspacesWorkspaceIdAssetsImagesCollectionsGetError,
>(
  variables: SearchCollectionsInfiniteQueryVariables,
  options?: Omit<
    UseInfiniteQueryOptions<SearchCollectionsQuery, TError, TData>,
    'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
  >,
) => {
  const queryKeyVariables = buildSearchCollectionsQueryKeyVariables(variables);

  return useInfiniteQuery<SearchCollectionsQuery, TError, TData>({
    queryKey: ['SearchCollectionsInfinite', queryKeyVariables],
    queryFn: async ({ pageParam = 0 }) => {
      const { data, response } = await searchCollectionsV1WorkspacesWorkspaceIdAssetsImagesCollectionsGet({
        path: { workspace_id: variables.workspace_id },
        query: buildSearchCollectionsQuery({
          ...variables,
          offset: pageParam as number,
        }),
        throwOnError: true,
      });

      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      const pageSize = variables.limit || 12;
      const currentOffset = (allPages.length - 1) * pageSize;
      const nextOffset = currentOffset + pageSize;

      if (!lastPage?.data || lastPage.data.length < pageSize || nextOffset >= lastPage.total_count) {
        return undefined;
      }

      return nextOffset;
    },
    getPreviousPageParam: (_, allPages) => {
      return allPages.length <= 1 ? undefined : 0;
    },
    initialPageParam: 0,
    // Prevent re-fetching all cached pages simultaneously when revisiting a previously used filter.
    // useInfiniteQuery refetches every loaded page in parallel on stale cache; a short staleTime avoids that.
    staleTime: 30_000,
    ...options,
  });
};
